"use client";

import { useFloatingBoundary } from "@faire/slate/private/floating/FloatingBoundaryContext";
import { useFloatingContext } from "@faire/slate/private/floating/FloatingContext";
import { getFloatingBoundaryDetectOverflowOptions } from "@faire/slate/private/floating/getFloatingBoundaryDetectOverflowOptions";
import { getFloatingHideStyles } from "@faire/slate/private/floating/getFloatingHideStyles";
import { mergeFloatingDetectOverflowPadding } from "@faire/slate/private/floating/mergeFloatingDetectOverflowPadding";
import {
  FloatingAlignment,
  FloatingPosition,
} from "@faire/slate/private/floating/types";
import { getFloatingPlacement } from "@faire/slate/private/floating/utils";
import {
  autoUpdate,
  flip,
  hide,
  offset,
  shift,
  size,
  useDismiss,
  useFloating,
  useInteractions,
  useRole,
  UseRoleProps,
  type ElementProps,
} from "@floating-ui/react";
import React, { useEffect, useLayoutEffect, useMemo } from "react";

import { POPUP_OFFSET, POPUP_FLIP_DETECTION_OFFSET } from "./consts";

type IProps = {
  /**
   * The alignment of the popup.
   */
  alignment: FloatingAlignment;

  /**
   * The props for the floating interactions.
   */
  interactions: ElementProps[];

  /**
   * The role of the popup.
   *
   * Reference: https://floating-ui.com/docs/userole#role
   */
  role?: UseRoleProps["role"];
  /**
   * The position of the popup.
   */
  position: FloatingPosition;

  /**
   * When true, the floating surface is at least as wide as the reference element and
   * grows with its content (capped by available viewport width).
   */
  minWidthMatchReference?: boolean;

  /**
   * When set, max height is `min(<viewport space>px, var(--your-var))`. Use for caps defined in CSS
   * (e.g. combobox list row count × row min-height).
   */
  maxHeightVar?: string;
};

export const PopupRoot: React.FC<React.PropsWithChildren<IProps>> = ({
  children,
  alignment,
  interactions: externalInteractions,
  role: roleProp,
  position,
  minWidthMatchReference = false,
  maxHeightVar,
}) => {
  const { floatingRootContext, setFloatingStyles, setInteractions } =
    useFloatingContext();
  const boundary = useFloatingBoundary();
  const hasBoundary = boundary.element !== null;

  const middleware = useMemo(() => {
    const boundaryOptions = getFloatingBoundaryDetectOverflowOptions(boundary);
    const overflowOptions = hasBoundary
      ? {
          ...boundaryOptions,
          padding: mergeFloatingDetectOverflowPadding(
            POPUP_FLIP_DETECTION_OFFSET,
            boundary.padding
          ),
        }
      : {};

    const items = [
      offset({ mainAxis: POPUP_OFFSET }),
      flip({
        fallbackAxisSideDirection: "start",
        padding: POPUP_FLIP_DETECTION_OFFSET,
        ...overflowOptions,
      }),
      shift({
        padding: POPUP_FLIP_DETECTION_OFFSET,
        ...overflowOptions,
      }),
      size({
        apply({ elements, availableWidth, availableHeight, rects }) {
          const available = Math.max(0, availableHeight);
          const maxHeight =
            maxHeightVar !== undefined
              ? `min(${available}px, var(${maxHeightVar.trim()}))`
              : `${available}px`;
          const styles: Record<string, string> = {
            maxHeight,
          };
          if (minWidthMatchReference) {
            styles.minWidth = `${rects.reference.width}px`;
            styles.width = "max-content";
            styles.maxWidth = `${Math.max(0, availableWidth)}px`;
          }
          Object.assign(elements.floating.style, styles);
        },
        padding: POPUP_FLIP_DETECTION_OFFSET,
        ...overflowOptions,
      }),
    ];

    if (hasBoundary) {
      items.push(hide({ ...overflowOptions }));
    }

    return items;
  }, [boundary, hasBoundary, minWidthMatchReference, maxHeightVar]);

  // we are not using the refs from the useFloating hook because we want to use the refs from the useFloatingContext hook
  const { floatingStyles, context, middlewareData } = useFloating({
    rootContext: floatingRootContext,
    placement: getFloatingPlacement(position, alignment),
    whileElementsMounted: autoUpdate,
    middleware,
  });

  const dismiss = useDismiss(context);
  const role = useRole(context, { role: roleProp });

  const interactions = useInteractions([
    role,
    dismiss,
    ...externalInteractions,
  ]);

  const referenceHidden = middlewareData.hide?.referenceHidden;

  useLayoutEffect(() => {
    setFloatingStyles({
      ...floatingStyles,
      ...(hasBoundary ? getFloatingHideStyles({ referenceHidden }) : {}),
    });
  }, [hasBoundary, floatingStyles, referenceHidden, setFloatingStyles]);

  useEffect(() => {
    setInteractions(interactions);
  }, [interactions, setInteractions]);

  return children;
};
